import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CarouselComponent, OwlOptions } from 'ngx-owl-carousel-o';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HomePageData } from 'src/app/models/homepage.types';
import { SeoService } from 'src/app/seo.service';
import { HomeService } from './home.service';
import {
  LocationSelection,
  LocationTypeaheadComponent,
} from 'src/app/shared/components/location-typeahead/location-typeahead.component';
import { CalendarOptions } from 'src/app/shared/components/inline-calendar/inline-calendar.component';
import {
  Guests,
  GuestType,
} from 'src/app/shared/components/guest-picker/guest-picker.types';
import {
  LocationLookupService,
  LocationSuggestionGroups,
  LocationSuggestionItem,
} from 'src/app/shared/services/location-lookup.service';
import { DEFAULT_LOCATION_EMPTY_STATE_SUGGESTIONS } from 'src/app/shared/services/location-default-suggestions';
import { SearchDialogService } from 'src/app/services/search-dialog.service';

interface RecentlyViewedPropertyCard {
  id: number;
  slug: string;
  t: string;
  ty: string;
  fn: string;
  avg: number;
  avga: number;
  visitedAt: number;
}

interface RecentlyViewedTrackingEvent {
  event: 'impression' | 'click';
  propertyId?: number;
  timestamp: number;
}

const RECENTLY_VIEWED_PROPERTIES_KEY = 'mxv_recently_viewed_properties';
const MAX_RECENTLY_VIEWED_PROPERTIES = 12;
const RECENTLY_VIEWED_MAX_AGE_DAYS = 30;
const RECENTLY_VIEWED_TRACKING_KEY = 'mxv_recently_viewed_tracking_events';
const RECENTLY_VIEWED_IMPRESSION_MARKER_KEY =
  'mxv_recently_viewed_last_impression_day';
const MAX_RECENTLY_VIEWED_TRACKING_EVENTS = 300;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  encapsulation: ViewEncapsulation.None,
  // animations: fuseAnimations,
  standalone: false,
})
export class HomeComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked
{
  @ViewChild('recentlyViewedOwl')
  recentlyViewedOwl?: CarouselComponent;

  @ViewChild('homeWhereTypeahead')
  homeWhereTypeahead?: LocationTypeaheadComponent;

  public isBrowser = isPlatformBrowser(this.platformId);
  showCalendar = false;

  public btnDatesValue = 'Select Your Dates';
  loaded = false;
  private dateRange = '';

  myForm: UntypedFormGroup;
  useMobileDates = false;

  homePageInfo: {
    posts: any;
    featured: any;
  };

  homePageInfo$: Observable<HomePageData>;
  recentlyViewedProperties: RecentlyViewedPropertyCard[] = [];
  recentlyViewedCarouselOptions: OwlOptions = {
    loop: false,
    rewind: false,
    slideBy: 1,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 600,
    navText: [
      '<i class="fa fa-arrow-left" aria-hidden="true"></i>',
      '<i class="fa fa-arrow-right" aria-hidden="true"></i>',
    ],
    nav: true,
    margin: 12,
    responsive: {
      0: {
        items: 1,
        stagePadding: 0,
      },
      640: {
        items: 2,
        stagePadding: 0,
      },
      992: {
        items: 3,
        stagePadding: 0,
      },
    },
  };
  private hasTrackedRecentlyViewedImpression = false;
  private recentSwipeStartX = 0;
  private recentSwipeStartY = 0;
  private recentSwipeGestureActive = false;
  private recentSwipeHandledByOwl = false;

  selectedDates: Date[];

  // range: NbCalendarRange<Date>;
  nonMobileRange = '';

  adults: number;
  children: number;
  infants: number;
  totalGuests: number;
  checkInCheckOut: string;
  searchLocation = '';
  selectedLocation: LocationSelection | null = null;
  homeEmptyStateSuggestions: LocationSelection[] = [
    ...DEFAULT_LOCATION_EMPTY_STATE_SUGGESTIONS,
  ] as LocationSelection[];
  searchModel = '';
  paropida = '';
  placeId = '';

  showMyElement = false;

  randomPhotoNumber = 0;

  minDate: any;
  blocked: any;
  showOptions: boolean;

  smallDevice = false;
  heroImage = '';
  desktopActivePanel: 'where' | 'when' | 'who' | null = null;
  mobileSearchOpen = false;
  mobileActivePanel: 'where' | 'when' | 'who' = 'where';
  mobileWhereSuggestionsExpanded = false;
  pendingLocationSelection: LocationSelection | null = null;
  guestSelection: GuestType = new Guests();
  dateFlexibility = 0;
  flexibilityOptions = [
    { days: 0, label: 'Exact dates' },
    { days: 1, label: '± 1 day' },
    { days: 2, label: '± 2 days' },
    { days: 3, label: '± 3 days' },
    // { days: 7, label: '± 7 days' },
  ];
  calendarOptions: CalendarOptions = {
    showTitle: false,
    multiple: true,
    minstays: [],
  };

  startDate: Date;
  endDate: Date;

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  constructor(
    @Inject(PLATFORM_ID) private platformId: {},

    private _homeService: HomeService,
    private seoService: SeoService,
    private router: Router,
    private observer: BreakpointObserver,
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private locationLookupService: LocationLookupService,
    private searchDialogService: SearchDialogService,
  ) {
    this.homePageInfo = {
      posts: [],
      featured: [],
    };
    this.checkInCheckOut = '';
    this.adults = 2;
    this.children = 0;
    this.infants = 0;
    this.totalGuests = 2;
    this.guestSelection.adults = 2;
    this.showOptions = false;

    this.randomPhotoNumber = this.randomInteger(0, 2);
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterContentChecked(): void {
    this.loaded = true;
  }
  ngAfterViewInit(): void {}

  ngOnInit() {
    this.homePageInfo$ = this._homeService.homePageData$;
    this.loadRecentlyViewedProperties();

    this.myForm = this._formBuilder.group({
      location: [''],
      // rememberme: [false]
    });

    this.myForm
      .get('location')
      .valueChanges.pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value) => {
        const currentValue = (value || '').toString().trim().toLowerCase();
        const selectedValue = (this.selectedLocation?.name || '')
          .toString()
          .trim()
          .toLowerCase();

        if (this.selectedLocation && currentValue !== selectedValue) {
          this.selectedLocation = null;
        }

        if (!currentValue) {
          this.pendingLocationSelection = null;
        }
      });

    this.seoService.setTitle(
      'Holiday Rentals, Luxury Villas, Apartments, Vacation - Myexclusive Villas',
    );
    this.seoService.setCanonicalURL();
    this.seoService.generateTags({
      title:
        'Holiday Rentals, Luxury Villas, Apartments, Vacation - Myexclusive Villas',
      description:
        'Uncover the best accomodations and experience your vacation in style. Holiday rentals, apartments and villas.  ',
      image: 'https://myexclusivevillas.com/assets/img/infinity-gwh.JPG',
      slug: '',
    });

    this.seoService.updateTag({
      name: 'description',
      content:
        'Uncover the best accomodations and experience your vacation in style. Holiday rentals, apartments and villas.',
    });

    this.observer
      .observe('(max-width: 784px)')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((result) => {
        this.smallDevice = result.matches;
        this.desktopActivePanel = null;

        if (!this.smallDevice) {
          this.mobileSearchOpen = false;
          this.setMobileSearchBodyState(false);
        }

        if (this.isBrowser) {
          this.getHomePageInfo();
        }
      });
  }

  onSearch(key): void {
    this.router.navigateByUrl(
      `/search/${key}/rentals?dates=${this.dateRange}&adults=${this.adults}&children=${this.children}&infants=${this.infants}&place_id=${this.placeId}`,
    );
  }

  getHomePageInfo(): void {
    // Get all info for home page

    this._homeService
      .showHomeInfo(this.smallDevice)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.loadRecentlyViewedProperties();
        // Mark for check
        this._changeDetectorRef.markForCheck();
      });
  }

  hasRecentlyViewedProperties(): boolean {
    return this.recentlyViewedProperties.length >= 3;
  }

  onRecentlyViewedClick(item: RecentlyViewedPropertyCard): void {
    if (!item?.id) {
      return;
    }

    this.trackRecentlyViewedEvent({
      event: 'click',
      propertyId: item.id,
      timestamp: Date.now(),
    });
  }

  getRecentlyViewedImage(item: RecentlyViewedPropertyCard): string {
    if (!item?.fn) {
      return '/assets/img/infinity-gwh.JPG';
    }

    return `https://myexclusivevillas.com/storage/p/${item.id}/${item.fn}`;
  }

  getRecentlyViewedPrice(item: RecentlyViewedPropertyCard): number {
    return Number(item?.avga || 0);
  }

  onRecentlyViewedTouchStart(event: TouchEvent): void {
    const touch = event.touches?.[0];
    if (!touch) {
      return;
    }

    this.recentSwipeGestureActive = true;
    this.recentSwipeHandledByOwl = false;
    this.recentSwipeStartX = touch.clientX;
    this.recentSwipeStartY = touch.clientY;
  }

  onRecentlyViewedTranslated(): void {
    if (this.recentSwipeGestureActive) {
      this.recentSwipeHandledByOwl = true;
    }
  }

  onRecentlyViewedTouchEnd(event: TouchEvent): void {
    const touch = event.changedTouches?.[0];
    if (!touch || !this.recentlyViewedOwl) {
      this.recentSwipeGestureActive = false;
      return;
    }

    const deltaX = touch.clientX - this.recentSwipeStartX;
    const deltaY = touch.clientY - this.recentSwipeStartY;

    if (this.recentSwipeHandledByOwl) {
      this.recentSwipeGestureActive = false;
      return;
    }

    // Only react to deliberate horizontal swipe gestures.
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
      this.recentSwipeGestureActive = false;
      return;
    }

    if (deltaX < 0) {
      this.recentlyViewedOwl.next();
    } else {
      this.recentlyViewedOwl.prev();
    }

    this.recentSwipeGestureActive = false;
  }

  async onSubmit(): Promise<void> {
    this.placeId = '';

    const locationValue = (this.myForm.get('location')?.value || '').trim();

    // If numeric property ID, navigate directly to property page instead of search
    if (/^\d+$/.test(locationValue)) {
      this.router.navigateByUrl(`/rental/${locationValue}`);
      this.desktopActivePanel = null;
      this.mobileSearchOpen = false;
      this.mobileActivePanel = 'where';
      this.setMobileSearchBodyState(false);
      return;
    }

    const keyWords =
      locationValue.length > 0
        ? locationValue
            .replace(/\s+/g, '-')
            .replace(/,+/g, '-')
            .replace(/--+/g, '-')
        : 'Anywhere';

    const searchParams: any = {
      dates: this.dateRange,
      adults: this.adults,
      children: this.children,
      infants: this.infants,
      dateFlexibility: this.dateFlexibility,
      place_id: this.placeId,
    };

    if (
      !this.selectedLocation &&
      locationValue &&
      !/^\d+$/.test(locationValue)
    ) {
      searchParams.searchKeyword = locationValue;
    }

    if (this.selectedLocation?.type === 'region' && this.selectedLocation.id) {
      searchParams.regionId = this.selectedLocation.id;
    }
    if (
      this.selectedLocation?.type === 'subregion' &&
      this.selectedLocation.id
    ) {
      searchParams.subregionId = this.selectedLocation.id;
    }
    if (
      this.selectedLocation?.type === 'propertyType' &&
      this.selectedLocation.id
    ) {
      searchParams.propertyTypeId = this.selectedLocation.id;
    }
    if (
      this.selectedLocation?.type === 'keyword' &&
      !/^\d+$/.test(this.selectedLocation.name || '')
    ) {
      searchParams.searchKeyword = this.selectedLocation.name;
    }
    if (
      this.selectedLocation?.type === 'property' &&
      this.selectedLocation.id
    ) {
      searchParams.propertyId = this.selectedLocation.id;
    }
    if (this.selectedLocation?.type === 'mapping' && this.selectedLocation.id) {
      searchParams.mappingId = this.selectedLocation.id;
      searchParams.place_id = this.selectedLocation.id;
    }

    this.router.navigateByUrl(
      `/search/${keyWords}/rentals?${new URLSearchParams(searchParams).toString()}`,
    );

    this.desktopActivePanel = null;
    this.mobileSearchOpen = false;
    this.mobileActivePanel = 'where';
    this.setMobileSearchBodyState(false);
  }

  onInputValueChanged(e): void {
    this.searchLocation = e;
    this.searchLocation = this.searchLocation
      .replace(/\s+/g, '-')
      .replace(/,+/g, '-');

    if (
      this.selectedLocation &&
      this.selectedLocation.name.toLowerCase() !== (e || '').toLowerCase()
    ) {
      this.selectedLocation = null;
    }
  }

  onLocationSelected(selection: LocationSelection): void {
    // If a specific property is selected, navigate directly to its page
    if (selection.type === 'property' && selection.id) {
      const slug =
        selection.name
          ?.toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '') || 'property';
      this.router.navigateByUrl(`/rental/${selection.id}/${slug}`);
      return;
    }

    this.selectedLocation = selection;
    this.pendingLocationSelection = selection;

    if (this.smallDevice) {
      this.mobileActivePanel = 'when';
      return;
    }

    this.desktopActivePanel = 'when';
  }

  onLocationFirstSuggestionChanged(selection: LocationSelection | null): void {
    this.pendingLocationSelection = selection;
  }

  handleAddressChange(address: any): void {
    this.searchLocation = address.name;
    this.searchLocation = this.searchLocation
      .replace(/\s+/g, '-')
      .replace(/,+/g, '-');
  }

  randomInteger(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  activateDesktopPanel(panel: 'where' | 'when' | 'who'): void {
    if (this.smallDevice) {
      return;
    }

    this.desktopActivePanel = panel;
    if (panel === 'where') {
      setTimeout(() => this.homeWhereTypeahead?.focusInput(), 50);
    }
  }

  openMobileSearch(): void {
    this.searchDialogService.open();
  }

  closeMobileSearch(): void {
    this.mobileSearchOpen = false;
    this.mobileActivePanel = 'where';
    this.mobileWhereSuggestionsExpanded = false;
    this.setMobileSearchBodyState(false);
  }

  setMobilePanel(panel: 'where' | 'when' | 'who'): void {
    this.mobileActivePanel = panel;

    if (panel === 'where') {
      this.mobileWhereSuggestionsExpanded = false;
    }
  }

  advanceMobilePanel(): void {
    if (this.mobileActivePanel === 'where') {
      this.mobileActivePanel = 'when';
      return;
    }

    if (this.mobileActivePanel === 'when') {
      this.mobileActivePanel = 'who';
      return;
    }

    this.onSubmit();
  }

  resetSearch(): void {
    this.myForm.reset({ location: '' });
    this.selectedLocation = null;
    this.pendingLocationSelection = null;
    this.placeId = '';
    this.dateRange = '';
    this.startDate = null;
    this.endDate = null;
    this.adults = 2;
    this.children = 0;
    this.infants = 0;
    this.totalGuests = 2;
    this.guestSelection = new Guests();
    this.guestSelection.adults = 2;
    this.dateFlexibility = 0;
    this.desktopActivePanel = this.smallDevice ? this.desktopActivePanel : null;
    this.mobileActivePanel = 'where';
    this.mobileWhereSuggestionsExpanded = false;
  }

  get mobileSuggestedDestinations(): LocationSelection[] {
    return this.homeEmptyStateSuggestions.filter(
      (item) =>
        item.type === 'region' ||
        item.type === 'subregion' ||
        item.type === 'mapping',
    );
  }

  get visibleMobileSuggestedDestinations(): LocationSelection[] {
    return this.mobileWhereSuggestionsExpanded
      ? this.mobileSuggestedDestinations
      : this.mobileSuggestedDestinations.slice(0, 5);
  }

  get showMobileSuggestedDestinations(): boolean {
    const value = (this.myForm?.get('location')?.value || '').toString().trim();
    return !value;
  }

  toggleMobileWhereSuggestions(): void {
    this.mobileWhereSuggestionsExpanded = !this.mobileWhereSuggestionsExpanded;
  }

  onMobileSuggestedDestinationSelected(selection: LocationSelection): void {
    this.onLocationSelected(selection);
    this.mobileWhereSuggestionsExpanded = false;
  }

  getMobileSuggestionIcon(suggestion: LocationSelection): string {
    switch (suggestion.type) {
      case 'region':
        return 'fa-map';
      case 'mapping':
        return 'fa-globe';
      case 'propertyType':
        return 'fa-home';
      default:
        return 'fa-map-marker';
    }
  }

  getMobileSuggestionIconClass(suggestion: LocationSelection): string {
    switch (suggestion.type) {
      case 'region':
        return 'is-region';
      case 'mapping':
        return 'is-mapping';
      case 'propertyType':
        return 'is-property-type';
      default:
        return 'is-subregion';
    }
  }

  onGuestsChanged(guests: GuestType): void {
    this.guestSelection = guests;
    this.adults = guests.adults ?? 0;
    this.children = guests.children ?? 0;
    this.infants = guests.infants ?? 0;
    this.totalGuests = this.adults + this.children + this.infants;
  }

  getWhereSummary(): string {
    const locationValue = (this.myForm.get('location')?.value || '').trim();
    return (
      this.selectedLocation?.name || locationValue || 'Search destinations'
    );
  }

  getWhenSummary(): string {
    if (!this.startDate || !this.endDate) {
      return 'Add dates';
    }

    return `${this.formatShortDate(this.startDate)} - ${this.formatShortDate(this.endDate)}`;
  }

  getWhenFlexibilitySummary(): string {
    return this.dateFlexibility > 0 ? `±${this.dateFlexibility} days` : '';
  }

  getWhoSummary(): string {
    return this.totalGuests > 0
      ? `${this.totalGuests} guest${this.totalGuests === 1 ? '' : 's'}`
      : 'Add guests';
  }

  getMobileNextLabel(): string {
    return this.mobileActivePanel === 'who' ? 'Search' : 'Next';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.smallDevice || !this.desktopActivePanel) {
      return;
    }

    const target = event.target as HTMLElement;

    // Fix: If the element was removed from the DOM (like redrawn calendar days or switching panels), ignore the click
    if (!document.body.contains(target)) {
      return;
    }

    if (
      target.closest('.home-airbnb-search') ||
      target.closest('.cdk-overlay-pane') ||
      target.closest('.cdk-overlay-container')
    ) {
      return;
    }

    this.desktopActivePanel = null;
  }

  onCalendarVisibilityChanged(val: { visible: boolean }): void {
    this.showCalendar = val.visible;
  }

  onDatesSelectedT(dt: { from: string; to: string }) {
    this.dateRange = `${dt.from}_${dt.to}`;
    this.startDate = new Date(dt.from);
    this.endDate = new Date(dt.to);

    if (this.smallDevice) {
      this.mobileActivePanel = 'when';
      return;
    }

    this.desktopActivePanel = 'when';
  }

  setDateFlexibility(days: number): void {
    this.dateFlexibility = days;
  }

  onDesktopDatesNext(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.desktopActivePanel = 'who';
  }

  private applySuggestedLocationFallback(): void {
    const locationValue = (this.myForm.get('location')?.value || '').trim();

    if (!locationValue || this.selectedLocation) {
      return;
    }

    if (this.pendingLocationSelection) {
      this.selectedLocation = this.pendingLocationSelection;
      this.myForm
        .get('location')
        ?.setValue(this.pendingLocationSelection.name, {
          emitEvent: false,
        });
      return;
    }

    if (/^\d+$/.test(locationValue)) {
      const propertyId = Number(locationValue);
      this.selectedLocation = {
        type: 'property',
        id: propertyId,
        name: locationValue,
        sublabel: 'Property ID',
      };
      this.pendingLocationSelection = this.selectedLocation;
      return;
    }

    this.selectedLocation = {
      type: 'keyword',
      name: locationValue,
      sublabel: 'Search in title, description and features',
    };
  }

  private async ensureSelectedLocationHasId(): Promise<void> {
    const selection = this.selectedLocation;
    if (!selection || selection.id || selection.type === 'keyword') {
      return;
    }

    const lookupTerm = (selection.name || '').trim();
    if (lookupTerm.length < 2) {
      return;
    }

    try {
      const results = await firstValueFrom(
        this.locationLookupService.searchTerms(lookupTerm),
      );
      const candidates = this.getCandidatesForType(results, selection.type);

      if (!candidates.length) {
        return;
      }

      const normalizedTerm = this.normalizeLocationLabel(lookupTerm);
      const resolved =
        candidates.find(
          (item) => this.normalizeLocationLabel(item.name) === normalizedTerm,
        ) ||
        candidates.find((item) => {
          const normalizedName = this.normalizeLocationLabel(item.name);
          return (
            normalizedName.includes(normalizedTerm) ||
            normalizedTerm.includes(normalizedName)
          );
        });

      if (!resolved) {
        return;
      }

      this.selectedLocation = {
        ...selection,
        id: resolved.id,
        sublabel: selection.sublabel || resolved.sublabel,
      };
      this.pendingLocationSelection = this.selectedLocation;
    } catch {
      // Keep existing selection when lookup fails; submit continues with fallback behavior.
    }
  }

  private getCandidatesForType(
    groups: LocationSuggestionGroups,
    type: LocationSelection['type'],
  ): LocationSuggestionItem[] {
    switch (type) {
      case 'region':
        return groups.regions || [];
      case 'subregion':
        return groups.subregions || [];
      case 'propertyType':
        return groups.propertyTypes || [];
      case 'property':
        return groups.properties || [];
      case 'mapping':
        return groups.mappings || [];
      default:
        return [];
    }
  }

  private normalizeLocationLabel(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private formatShortDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  private setMobileSearchBodyState(open: boolean): void {
    if (!this.isBrowser) {
      return;
    }

    document.body.classList.toggle('home-mobile-search-open', open);
  }

  private loadRecentlyViewedProperties(): void {
    if (!this.isBrowser) {
      this.recentlyViewedProperties = [];
      return;
    }

    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_PROPERTIES_KEY);
      if (!raw) {
        this.recentlyViewedProperties = [];
        return;
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        this.recentlyViewedProperties = [];
        return;
      }

      this.recentlyViewedProperties = parsed
        .filter((item) => item && Number(item.id) > 0)
        .map((item) => ({
          id: Number(item.id),
          slug: String(item.slug || ''),
          t: String(item.t || ''),
          ty: String(item.ty || ''),
          fn: String(item.fn || ''),
          avg: Number(item.avg || 0),
          avga: Number(item.avga || 0),
          visitedAt: Number(item.visitedAt || 0),
        }))
        .filter((item) => this.isRecentlyViewedFresh(item.visitedAt))
        .sort((a, b) => b.visitedAt - a.visitedAt)
        .slice(0, MAX_RECENTLY_VIEWED_PROPERTIES);

      // Keep storage trimmed and clean so it never grows beyond the cap.
      localStorage.setItem(
        RECENTLY_VIEWED_PROPERTIES_KEY,
        JSON.stringify(this.recentlyViewedProperties),
      );

      if (
        this.hasRecentlyViewedProperties() &&
        !this.hasTrackedRecentlyViewedImpression
      ) {
        this.trackRecentlyViewedImpressionOncePerDay();
        this.hasTrackedRecentlyViewedImpression = true;
      }
    } catch {
      this.recentlyViewedProperties = [];
    }
  }

  private isRecentlyViewedFresh(visitedAt: number): boolean {
    if (!visitedAt || Number.isNaN(visitedAt)) {
      return false;
    }

    const maxAgeMs = RECENTLY_VIEWED_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - visitedAt <= maxAgeMs;
  }

  private trackRecentlyViewedImpressionOncePerDay(): void {
    const impressionDay = new Date().toISOString().slice(0, 10);
    const lastImpressionDay = localStorage.getItem(
      RECENTLY_VIEWED_IMPRESSION_MARKER_KEY,
    );

    if (lastImpressionDay === impressionDay) {
      return;
    }

    this.trackRecentlyViewedEvent({
      event: 'impression',
      timestamp: Date.now(),
    });

    localStorage.setItem(RECENTLY_VIEWED_IMPRESSION_MARKER_KEY, impressionDay);
  }

  private trackRecentlyViewedEvent(event: RecentlyViewedTrackingEvent): void {
    if (!this.isBrowser) {
      return;
    }

    let events: RecentlyViewedTrackingEvent[] = [];

    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_TRACKING_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) {
        events = parsed;
      }
    } catch {
      events = [];
    }

    events.push(event);

    if (events.length > MAX_RECENTLY_VIEWED_TRACKING_EVENTS) {
      events = events.slice(
        events.length - MAX_RECENTLY_VIEWED_TRACKING_EVENTS,
      );
    }

    localStorage.setItem(RECENTLY_VIEWED_TRACKING_KEY, JSON.stringify(events));

    if ((window as any)?.dataLayer?.push) {
      (window as any).dataLayer.push({
        event: `view_again_${event.event}`,
        section: 'view_again',
        propertyId: event.propertyId,
        timestamp: event.timestamp,
      });
    }
  }

  ngOnDestroy(): void {
    this.setMobileSearchBodyState(false);
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
